import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { TenantThemeConfig } from '../types/theme'
import {
  getCashbackDisplayPromotionConfig,
  type UserCashbackDisplaySettings,
} from '../services/promotionConfigService'
import {
  claimCashbackSettlement,
  getCashbackHistory,
  getCashbackMe,
  type MemberCashbackSettlement,
  type MemberCashbackSummary,
} from '../services/cashbackService'
import * as authService from '../services/authService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernCashbackPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const defaultDisplay: UserCashbackDisplaySettings = {
  pageTitle: 'เงินคืนยอดเล่น',
  pageSubtitle: 'ตรวจสอบยอดคืนล่าสุด กดรับ cashback ได้ทันที และดูเงื่อนไขได้ในหน้าจอเดียว',
  heroBadgeText: 'Cashback Center',
  withdrawalButtonText: 'ถอนเงินแนะนำ',
  noticeText: 'รายได้การคืนยอดเล่นของท่านสามารถแจ้งถอนได้ทุกเวลา หากมียอดรายได้มากกว่า 15 บาทขึ้นไป',
  minWithdrawalAmount: 15,
  cashbackRateText: '0.3% - 0.8%',
  promotionalMessage: 'สุดยอดโปรโมชั่น รับเงินคืนฟรี ทันทีทุกวัน ทั้งวัน ไม่อั้น ไม่ติดเทิร์น ง่ายๆ',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบ cashback ปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่มีประวัติ cashback',
  showRateTable: true,
  showExample: true,
  promotionRoute: '/cashback',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'คืนยอดเสีย',
    badgeText: 'Cashback',
    title: 'คืนยอดเสีย',
    description: 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
    buttonText: 'ไปหน้า Cashback',
  },
}

const ModernCashbackPage: React.FC<ModernCashbackPageProps> = ({ themeConfig }) => {
  const [displayConfig, setDisplayConfig] = useState<UserCashbackDisplaySettings>(defaultDisplay)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cashbackDisabled, setCashbackDisabled] = useState(false)
  const [cashbackSummary, setCashbackSummary] = useState<MemberCashbackSummary>({
    claimableAmount: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    paidAmount: 0,
    totalEarned: 0,
  })
  const [latestClaimable, setLatestClaimable] = useState<MemberCashbackSettlement | null>(null)
  const [cashbackHistory, setCashbackHistory] = useState<MemberCashbackSettlement[]>([])
  const [claiming, setClaiming] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [claimMessage, setClaimMessage] = useState('')
  const refreshCashbackConfigRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshCashbackDataRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadCashbackDisplayConfig = useCallback(async () => {
    try {
      const configRes = await getCashbackDisplayPromotionConfig(themeConfig.tenantId)
      const nextSettings = configRes?.data?.settings
      if (configRes?.success && nextSettings) {
        setDisplayConfig((prev) => ({
          ...prev,
          ...nextSettings,
        }))
      } else {
        setDisplayConfig(defaultDisplay)
      }
    } catch {
      setDisplayConfig(defaultDisplay)
    }
  }, [themeConfig.tenantId])

  const loadCashbackData = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true)
    }
    setError(null)

    try {
      const [meRes, historyRes] = await Promise.all([
        getCashbackMe(),
        getCashbackHistory(10),
      ])

      setCashbackDisabled(false)
      if (meRes?.success) {
        setCashbackSummary(meRes.data.summary)
        setLatestClaimable(meRes.data.latestClaimable)
      }
      if (historyRes?.success) {
        setCashbackHistory(Array.isArray(historyRes.data) ? historyRes.data : [])
      }
    } catch (err) {
      const errorLike = err as { message?: string; status?: number }
      const isDisabled =
        errorLike?.status === 404 ||
        String(errorLike?.message || '').toLowerCase().includes('not enabled')

      if (isDisabled) {
        setCashbackDisabled(true)
        setCashbackSummary({
          claimableAmount: 0,
          pendingAmount: 0,
          approvedAmount: 0,
          paidAmount: 0,
          totalEarned: 0,
        })
        setLatestClaimable(null)
        setCashbackHistory([])
      } else {
        setError(err instanceof Error ? err.message : 'ไม่สามารถโหลดข้อมูล cashback ล่าสุดได้')
      }
    } finally {
      if (!options?.silent) {
        setLoading(false)
      }
      setRefreshing(false)
    }
  }, [displayConfig.disabledMessage, displayConfig.disabledTitle])

  useEffect(() => {
    let active = true

    const loadData = async () => {
      try {
        await loadCashbackDisplayConfig()
        if (!active) return
        await loadCashbackData()
      } finally {
        if (!active) return
      }
    }

    void loadData()

    return () => {
      active = false
    }
  }, [loadCashbackData, loadCashbackDisplayConfig])

  useEffect(() => {
    const handleCashbackConfigUpdate = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        contentType?: string
        type?: string
        promotionType?: string
        username?: string
        userId?: string
        content?: {
          promotionType?: string
          type?: string
          username?: string
          userId?: string
        }
      } | undefined

      const isCashbackSystemUpdate =
        detail?.type === 'cashback_config_updated' ||
        detail?.promotionType === 'cashback' ||
        detail?.content?.type === 'cashback_config_updated' ||
        detail?.content?.promotionType === 'cashback' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'cashback' ||
          detail?.content?.promotionType === 'cashback'
        ))

      const isCashbackDisplayUpdate =
        detail?.type === 'cashback_display_config_updated' ||
        detail?.promotionType === 'cashback_display' ||
        detail?.content?.type === 'cashback_display_config_updated' ||
        detail?.content?.promotionType === 'cashback_display' ||
        (detail?.contentType === 'promotion_config' && (
          detail?.promotionType === 'cashback_display' ||
          detail?.content?.promotionType === 'cashback_display'
        ))

      const isCashbackSettlementUpdate =
        detail?.type === 'cashback_settlement_updated' ||
        detail?.content?.type === 'cashback_settlement_updated'

      const currentUser = authService.getCurrentUser()
      const currentUsername = String(currentUser?.username || '').trim().toLowerCase()
      const currentUserId = String(currentUser?.id || '').trim()
      const targetUsername = String(detail?.username || detail?.content?.username || '').trim().toLowerCase()
      const targetUserId = String(detail?.userId || detail?.content?.userId || '').trim()
      const isForCurrentMember =
        (!targetUsername && !targetUserId) ||
        (!!targetUsername && targetUsername === currentUsername) ||
        (!!targetUserId && !!currentUserId && targetUserId === currentUserId)

      if (!isCashbackSystemUpdate && !isCashbackDisplayUpdate && !isCashbackSettlementUpdate) return
      if (isCashbackSettlementUpdate && !isForCurrentMember) return

      if (isCashbackSystemUpdate || isCashbackDisplayUpdate) {
        if (refreshCashbackConfigRef.current) {
          clearTimeout(refreshCashbackConfigRef.current)
        }
        refreshCashbackConfigRef.current = setTimeout(() => {
          void loadCashbackDisplayConfig().catch(() => {})
        }, 250)
      }

      if (isCashbackSystemUpdate || isCashbackSettlementUpdate) {
        if (refreshCashbackDataRef.current) {
          clearTimeout(refreshCashbackDataRef.current)
        }
        refreshCashbackDataRef.current = setTimeout(() => {
          void loadCashbackData({ silent: true }).catch(() => {})
        }, 250)
      }
    }

    window.addEventListener('content_update', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_config_updated', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_display_config_updated', handleCashbackConfigUpdate as EventListener)
    window.addEventListener('cashback_settlement_updated', handleCashbackConfigUpdate as EventListener)

    return () => {
      if (refreshCashbackConfigRef.current) {
        clearTimeout(refreshCashbackConfigRef.current)
        refreshCashbackConfigRef.current = null
      }
      if (refreshCashbackDataRef.current) {
        clearTimeout(refreshCashbackDataRef.current)
        refreshCashbackDataRef.current = null
      }
      window.removeEventListener('content_update', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_config_updated', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_display_config_updated', handleCashbackConfigUpdate as EventListener)
      window.removeEventListener('cashback_settlement_updated', handleCashbackConfigUpdate as EventListener)
    }
  }, [loadCashbackData, loadCashbackDisplayConfig])

  const tableRows = useMemo(() => [
    { label: 'ประเภทโบนัส', value: 'คืนยอดเล่นรายวัน' },
    { label: '% โบนัส จากยอดเล่น', value: displayConfig.cashbackRateText || '%' },
    { label: 'โบนัสที่สามารถขอได้', value: 'ตามยอดเสียสุทธิที่เข้าเงื่อนไข' },
    { label: 'ถอนได้สูงสุด', value: 'ขึ้นอยู่กับค่า config ของระบบ' },
    { label: 'เทิร์นกี่เท่าจากยอดเล่น', value: `${latestClaimable?.bonusLock?.turnoverMultiplier || 1}x` },
    { label: 'จำนวนเทิร์นที่จะติด', value: latestClaimable ? 'แสดงตามงวดที่พร้อมรับล่าสุด' : 'รอรายการพร้อมรับ' },
  ], [displayConfig.cashbackRateText, latestClaimable])

  const summaryCards = useMemo(() => ([
    { label: 'รอรับ', value: cashbackSummary.claimableAmount, accentClass: 'bg_gd_quaternary' },
    { label: 'รออนุมัติ', value: cashbackSummary.pendingAmount, accentClass: 'bg_gd_secondary' },
    { label: 'จ่ายแล้ว', value: cashbackSummary.paidAmount, accentClass: 'bg_primary' },
    { label: 'รวมทั้งหมด', value: cashbackSummary.totalEarned, accentClass: 'bg_gd_secondary' },
  ]), [cashbackSummary.claimableAmount, cashbackSummary.pendingAmount, cashbackSummary.paidAmount, cashbackSummary.totalEarned])

  const formatCurrency = (value?: number) => new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  const formatDate = (value?: string) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('th-TH')
  }

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'claimable':
        return 'รอรับ'
      case 'pending':
        return 'รออนุมัติ'
      case 'approved':
        return 'อนุมัติแล้ว'
      case 'paid':
        return 'จ่ายแล้ว'
      case 'rejected':
        return 'ไม่อนุมัติ'
      case 'payment_failed':
        return 'จ่ายไม่สำเร็จ'
      case 'expired':
        return 'หมดอายุ'
      default:
        return status || '-'
    }
  }

  const refreshCashbackData = async () => {
    try {
      setRefreshing(true)
      setClaimMessage('')
      await loadCashbackDisplayConfig()
      await loadCashbackData({ silent: true })
    } catch {
      setRefreshing(false)
      setClaimMessage('รีเฟรชข้อมูล cashback ไม่สำเร็จ')
    }
  }

  const handleClaimCashback = async () => {
    if (!latestClaimable?._id || claiming || cashbackDisabled) return
    try {
      setClaiming(true)
      setClaimMessage('')
      await claimCashbackSettlement(latestClaimable._id)
      setClaimMessage('ส่งคำขอรับ cashback สำเร็จแล้ว')
      await refreshCashbackData()
    } catch (error) {
      setClaimMessage(error instanceof Error ? error.message : 'ไม่สามารถรับ cashback ได้')
    } finally {
      setClaiming(false)
    }
  }

  const renderDisabledNotice = () => (
    <div className="service-disabled-notice light_sd border_10 bg_op_secondary bg_blur">
      <div className="service-disabled-notice__icon" aria-hidden>
        <i className="fas fa-pause-circle" />
      </div>
      <h4 className="service-disabled-notice__title c_tertiary">{displayConfig.disabledTitle}</h4>
      <p className="service-disabled-notice__message">{displayConfig.disabledMessage}</p>
    </div>
  )

  if (loading) {
    return (
      <div className="content">
        <div className="container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3 c_tertiary">กำลังโหลดข้อมูล cashback...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !cashbackDisabled) {
    return (
      <div className="content">
        <div className="container">
          <div className="text-center py-5">
            <p className="c_primary mb-0">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (cashbackDisabled) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="c_quinary text-center mb-2">
                <h3 className="c_tertiary font_md mb-0">{displayConfig.pageTitle}</h3>
              </div>
              {renderDisabledNotice()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="c_quinary text-center">
              <h3 className="c_tertiary font_md">{displayConfig.pageTitle}</h3>
              <p className="mb-0 mt-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                {displayConfig.pageSubtitle}
              </p>
            </div>

            <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3 position-relative overflow-hidden">
              <div
                className="border_10 p-3 p-md-4 mb-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(7,18,48,0.96), rgba(14,46,92,0.88))',
                  border: '1px solid rgba(78, 168, 255, 0.35)',
                }}
              >
                <div className="row g-3">
                  <div className="col-12 col-lg-7">
                    <div className="d-flex flex-column h-100 justify-content-between">
                      <div>
                        <div className="small text-uppercase mb-2" style={{ color: 'rgba(144, 205, 244, 0.9)', letterSpacing: '0.08em' }}>
                          {displayConfig.heroBadgeText}
                        </div>
                        <h4 className="c_tertiary mb-2">{displayConfig.pageTitle}</h4>
                        <p className="mb-0" style={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7 }}>
                          {displayConfig.noticeText}
                        </p>
                      </div>
                      <div className="row g-3 mt-2">
                        <div className="col-12 col-md-6">
                          <div className="border_10 p-3 h-100" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <div className="small mb-1" style={{ color: 'rgba(255,255,255,0.64)' }}>งวดที่พร้อมรับล่าสุด</div>
                            <div className="c_tertiary font_md">
                              {latestClaimable
                                ? `${formatDate(latestClaimable.periodStartAt)} - ${formatDate(latestClaimable.periodEndAt)}`
                                : 'ยังไม่มีรายการพร้อมรับ'}
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="border_10 p-3 h-100" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <div className="small mb-1" style={{ color: 'rgba(255,255,255,0.64)' }}>Turnover ที่ต้องทำ</div>
                            <div className="c_tertiary font_md">
                              {latestClaimable ? `${latestClaimable.bonusLock?.turnoverMultiplier || 1}x` : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-5">
                    <div
                      className="border_10 p-3 p-md-4 h-100 d-flex flex-column justify-content-center"
                      style={{
                        background: 'linear-gradient(180deg, rgba(15,23,42,0.9), rgba(17,24,39,0.72))',
                        border: '1px solid rgba(94, 234, 212, 0.18)',
                      }}
                    >
                      <div className="small text-center mb-2" style={{ color: 'rgba(255,255,255,0.68)' }}>
                        ยอดพร้อมรับตอนนี้
                      </div>
                      <div className="text-center c_tertiary" style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1 }}>
                        {latestClaimable ? formatCurrency(latestClaimable.calculation?.cashbackAmount) : '0.00'}
                      </div>
                      <div className="text-center mt-1" style={{ color: 'rgba(255,255,255,0.68)' }}>บาท</div>
                      <button
                        type="button"
                        onClick={handleClaimCashback}
                        disabled={!latestClaimable?._id || claiming || cashbackDisabled}
                        aria-disabled={!latestClaimable?._id || claiming || cashbackDisabled}
                        className="link_pmr w-100 box_row justify-content-center font_rg bg_gd_quaternary border_30 border-0 mt-3 py-2"
                        style={{
                          opacity: !latestClaimable?._id || claiming || cashbackDisabled ? 0.72 : 1,
                          boxShadow: '0 12px 30px rgba(72, 187, 255, 0.22)',
                        }}
                      >
                        <h4 className="c_tertiary mb-0">
                          {claiming
                            ? 'กำลังดำเนินการ...'
                            : latestClaimable?._id
                              ? displayConfig.withdrawalButtonText
                              : 'ยังไม่มีรายการ cashback ให้รับ'}
                        </h4>
                      </button>
                      <button
                        type="button"
                        onClick={refreshCashbackData}
                        disabled={refreshing}
                        className="btn btn-sm mt-2"
                        style={{
                          color: '#e2f3ff',
                          borderColor: 'rgba(148, 163, 184, 0.4)',
                          background: 'rgba(255,255,255,0.04)',
                        }}
                      >
                        {refreshing ? 'กำลังรีเฟรช...' : 'รีเฟรชข้อมูล'}
                      </button>
                      {claimMessage ? (
                        <p className="small text-center mt-3 mb-0" style={{ color: '#d7f3ff' }}>{claimMessage}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                {summaryCards.map((card) => (
                  <div key={card.label} className="col-6 col-md-3">
                    <div
                      className="border_10 p-3 text-center h-100"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <div className={`border_30 px-2 py-1 mb-2 d-inline-block ${card.accentClass}`}>
                        <span className="c_tertiary small">{card.label}</span>
                      </div>
                      <div className="h5 mb-0 c_tertiary">{formatCurrency(card.value)}</div>
                      <div className="small mt-1" style={{ color: 'rgba(255,255,255,0.58)' }}>บาท</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row g-4">
                <div className="col-12 col-xl-7">
                  <div
                    className="border_10 p-3 p-md-4 h-100"
                    style={{
                      background: 'rgba(7, 18, 48, 0.72)',
                      border: '1px solid rgba(78, 168, 255, 0.25)',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                      <div>
                        <h5 className="c_tertiary mb-1">เงื่อนไขและอัตราเงินคืน</h5>
                        <p className="mb-0" style={{ color: 'rgba(255,255,255,0.64)' }}>
                          แสดงรายละเอียดการรับ cashback ตามการตั้งค่าปัจจุบันของระบบ
                        </p>
                      </div>
                      <div className="px-3 py-2 border_30 bg_gd_secondary">
                        <span className="c_tertiary font_md">{displayConfig.cashbackRateText}</span>
                      </div>
                    </div>

                    {displayConfig.showRateTable && (
                      <div className="table-responsive">
                        <table
                          className="table mb-0 align-middle"
                          style={{
                            color: '#e6f4ff',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                          }}
                        >
                          <tbody>
                            {tableRows.map((row, i) => (
                              <tr key={i}>
                                <th
                                  scope="row"
                                  style={{
                                    width: '52%',
                                    color: '#f8fbff',
                                    background: 'rgba(255,255,255,0.06)',
                                    borderColor: 'rgba(255,255,255,0.08)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {row.label}
                                </th>
                                <td
                                  style={{
                                    color: row.value ? '#8ee7ff' : 'rgba(255,255,255,0.74)',
                                    background: 'rgba(7,18,48,0.55)',
                                    borderColor: 'rgba(255,255,255,0.08)',
                                  }}
                                >
                                  {row.value || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div
                      className="border_10 p-3 mt-3"
                      style={{
                        background: 'rgba(56, 189, 248, 0.10)',
                        border: '1px solid rgba(56, 189, 248, 0.22)',
                      }}
                    >
                      <div className="small mb-1" style={{ color: '#7dd3fc' }}>หมายเหตุ</div>
                      <p className="mb-0" style={{ color: '#ecfeff', lineHeight: 1.7 }}>
                        {displayConfig.noticeText}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-5">
                  <div
                    className="border_10 p-3 p-md-4 h-100"
                    style={{
                      background: 'rgba(7, 18, 48, 0.72)',
                      border: '1px solid rgba(94, 234, 212, 0.22)',
                    }}
                  >
                    <h5 className="c_tertiary mb-3">ภาพรวมการรับ cashback</h5>
                    {displayConfig.showExample && (
                      <div
                        className="border_10 p-3 mb-3"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <h6 className="c_tertiary mb-2">ตัวอย่างการคำนวณ</h6>
                        <div style={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.8 }}>
                          เล่นบาคาร่าสด
                          <br />
                          แทง Player 1,000,000 บาท ผลออกมาว่า Player ชนะ
                          <br />
                          แทง Player อีกครั้ง 1,000,000 บาท ผลออกมาว่า Banker ชนะ
                          <br />
                          รวมยอดเดิมพันทั้ง 2 ครั้ง คือ 2,000,000 บาท
                          <br />
                          ท่านจะได้รับเงินคืน {displayConfig.cashbackRateText}
                          <br />
                          รายได้ที่จะได้รับขึ้นอยู่กับเรทที่ tenant ตั้งค่าไว้
                          <br />
                          ไม่ว่าท่านจะเล่นแพ้หรือชนะ ก็ได้รับเงินคืนได้
                        </div>
                      </div>
                    )}

                    <div
                      className="border_10 p-3"
                      style={{
                        background: 'linear-gradient(135deg, rgba(34,197,94,0.20), rgba(14,165,233,0.16))',
                        border: '1px solid rgba(74, 222, 128, 0.20)',
                      }}
                    >
                      <div className="small text-uppercase mb-2" style={{ color: '#86efac', letterSpacing: '0.08em' }}>
                        Highlight
                      </div>
                      <h5 className="c_tertiary mb-0">
                        {displayConfig.promotionalMessage}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>

              {cashbackHistory.length > 0 ? (
                <div
                  className="border_10 p-3 p-md-4 mt-4"
                  style={{
                    background: 'rgba(7, 18, 48, 0.72)',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                    <div>
                      <h4 className="c_tertiary mb-1">ประวัติ Cashback ล่าสุด</h4>
                      <p className="mb-0" style={{ color: 'rgba(255,255,255,0.64)' }}>
                        แสดงรายการล่าสุดเพื่อให้ติดตามสถานะการรับ cashback ได้ง่ายขึ้น
                      </p>
                    </div>
                    <div className="small" style={{ color: 'rgba(255,255,255,0.62)' }}>
                      ทั้งหมด {cashbackHistory.length} รายการ
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle" style={{ color: '#e6f4ff' }}>
                      <thead>
                        <tr>
                          <th style={{ color: '#f8fbff', borderColor: 'rgba(255,255,255,0.08)' }}>ช่วงเวลา</th>
                          <th style={{ color: '#f8fbff', borderColor: 'rgba(255,255,255,0.08)' }}>ยอดเสียสุทธิ</th>
                          <th style={{ color: '#f8fbff', borderColor: 'rgba(255,255,255,0.08)' }}>เงินคืน</th>
                          <th style={{ color: '#f8fbff', borderColor: 'rgba(255,255,255,0.08)' }}>สถานะ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashbackHistory.map((item) => (
                          <tr key={item._id}>
                            <td style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                              {formatDate(item.periodStartAt)}
                              {' - '}
                              {formatDate(item.periodEndAt)}
                            </td>
                            <td style={{ borderColor: 'rgba(255,255,255,0.08)', color: '#fef3c7' }}>
                              {formatCurrency(item.sourceSummary?.netLoss)}
                            </td>
                            <td style={{ borderColor: 'rgba(255,255,255,0.08)', color: '#86efac', fontWeight: 600 }}>
                              {formatCurrency(item.calculation?.cashbackAmount)}
                            </td>
                            <td style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                              <span
                                className="border_30 px-2 py-1 d-inline-block"
                                style={{
                                  background: 'rgba(56, 189, 248, 0.12)',
                                  color: '#d7f3ff',
                                  border: '1px solid rgba(56, 189, 248, 0.18)',
                                }}
                              >
                                {getStatusLabel(item.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div
                  className="border_10 p-3 p-md-4 mt-4 text-center"
                  style={{
                    background: 'rgba(7, 18, 48, 0.52)',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                  }}
                >
                  <p className="mb-0" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {displayConfig.historyEmptyText}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernCashbackPage
